/**
 * 게이트 입력 — `OrderDetail.displayStatus`(BE 정본) 만 본다. DisplayStatus 유니온으로 좁히지
 * 않고 string|null|undefined 를 받아, 미지/누락 값도 안전히 false 처리한다.
 */
interface DisplayStatusBearing {
  displayStatus: string | null | undefined;
}

/**
 * D-2 출고 버튼 상태확정 게이트.
 *
 * BE 정본(`BE-ORDER-D-2-J-1-fulfillment-cancel-contract.md`): '상품준비중 처리'/출고실패 액션은
 * **PAYMENT_CONFIRMED(결제완료 displayStatus=PAID) 또는 READY_FOR_FULFILLMENT(상품준비중
 * displayStatus=PROCESSING)** 진입만 허용한다. 그 외(이미 READY_TO_SHIP/SHIPPED/DELIVERED 등
 * 출고가 진전됐거나 종결/취소/클레임 상태)에서는 BE 가 409 `ORDER_STATUS_TRANSITION_FORBIDDEN`
 * 으로 거절한다.
 *
 * FE 는 BE displayStatus(정본)를 재도출하지 않고 "이 액션이 적용 가능한 단계인지"만 판정해
 * 버튼 노출/활성을 결정한다(전이 후 자동 비활성). 권한(RBAC)·DELIVERY 여부는 호출측에서 별도 확인.
 *
 * ⚠ 16차 ID51 용어통일: 버튼 라벨이 '상품준비중 처리'(과거 '출고 준비 완료')로 정합됨. 게이트/전이
 * 로직·displayStatus 키는 BE 계약 그대로 무수정 — 라벨 문자열만 변경.
 */

/** 출고 액션('상품준비중 처리'/출고실패)이 의미 있는 진입 displayStatus 집합 (delivery 전제). */
const FULFILLMENT_ACTIONABLE_STATUSES: ReadonlySet<string> = new Set([
  'PAID', // orderStatus=PAYMENT_CONFIRMED, fulfillment_status=PENDING (결제완료)
  'PROCESSING', // orderStatus=READY_FOR_FULFILLMENT (상품준비중)
]);

/**
 * '상품준비중 처리' / 출고실패 버튼을 노출/활성할 수 있는 단계인지.
 * displayStatus 가 PAID/PROCESSING 일 때만 true → 출고 처리 후(READY_TO_SHIP/SHIPPED 등)에는
 * 버튼이 자동 비활성되어 재클릭(→ 409)을 막는다.
 */
export const canMarkFulfillment = (order: DisplayStatusBearing): boolean =>
  typeof order.displayStatus === 'string' &&
  FULFILLMENT_ACTIONABLE_STATUSES.has(order.displayStatus);

/**
 * 이미 출고가 진전된(배송대기/배송중/배송완료) DELIVERY 주문인지 — '처리됨' 안내 노출용.
 * 출고 액션이 더 이상 불가능함을 운영자에게 명시적으로 보여준다.
 */
const FULFILLMENT_PROGRESSED_STATUSES: ReadonlySet<string> = new Set([
  'READY_TO_SHIP', // pill 표기 '상품준비중'(고도몰 24엔 없는 독립몰 단계 흡수), 출고 처리 완료 단계
  'SHIPPED', // 배송중
  'DELIVERED', // 배송완료
]);

export const isFulfillmentProgressed = (order: DisplayStatusBearing): boolean =>
  typeof order.displayStatus === 'string' &&
  FULFILLMENT_PROGRESSED_STATUSES.has(order.displayStatus);
